import { Box, Flex, useToast } from "@chakra-ui/react";
import React, { Children, cloneElement, isValidElement, useEffect, useState } from "react";
import { useLocation, useNavigate } from 'react-router-dom';

import { $ok } from "foundation-ts/commons";
import { Nullable } from "foundation-ts/types";

import { SessionSharedData } from "@containers/toBeSignedDocuments/ExternalSignView";
import { config } from "@/config/config";
import { useColorContext } from "@hooks/useColorContext";
import { $g1Color } from "@/theme/G1Style";
import { defaultErrorToast } from "@utils/toast";

export interface TemporaryChildProps {
    sharedData?: Nullable<string|SessionSharedData> ;
}

interface TemporaryAuthenticatedLayoutProps {
    children: React.ReactNode ;
    getData: (token: string) => Promise<Nullable<string|SessionSharedData>> ;
};

export const TemporaryAuthenticatedLayout = ({ children, getData }: TemporaryAuthenticatedLayoutProps) => {
    const [showChildren, setShowChildren] = useState(false) ;
    const [sharedData, setSharedData] = useState<Nullable<string|SessionSharedData>>(null) ;
    const toast = useToast();
    const navigate = useNavigate();

    const { search } = useLocation() ;
    const searchParams = new URLSearchParams(search) ;
    const token = searchParams.get("token") ;

    const enhancedChildren = Children.map(children, child => {
        if (isValidElement<TemporaryChildProps>(child)) {
            return cloneElement<TemporaryChildProps>(child, { sharedData }) ;
        }
        return child ;
    }) ;

    useEffect(() => {
        const checkTokenAndRetrieveData = async () => {
            const data = await getData(token!) ;
            if ($ok(data)) { 
                setSharedData(data!) ;
            } else {
                toast(defaultErrorToast("Impossible de récupérer les informations nécessaires. Vous allez être automatiquement redirigé vers la page d'authentification.")) ;
                const timer = setTimeout(() => {
                    navigate(config.app.loginURL!) ;
                }, 2000) ;
    
                return () => clearTimeout(timer) ;
            }
        }
        setShowChildren($ok(token)) ;
        if (!$ok(token)) {
            toast(defaultErrorToast("Paramètre 'token' manquant. Vous allez être automatiquement redirigé vers la page d'authentification.")) ;
            const timer = setTimeout(() => {
                navigate(config.app.loginURL!) ;
            }, 2000) ;

            return () => clearTimeout(timer) ;
        } else {
            checkTokenAndRetrieveData() ;
        }
    }, []) ;

    useColorContext() ;
    return (
        <Box as="section" bg={$g1Color('global.bg')} px="0" py="0">
            <Box px={{ base: "0" }}>
                <Flex h="100vh" flexDirection="column">
                    <Flex flex="1" overflow="auto">
                        {showChildren && enhancedChildren}
                    </Flex>
                </Flex>
            </Box>
        </Box>
    ) ;
} ;